/**
 * Composer "Attach" menu (paperclip dropdown):
 *   Upload from computer · Documents · Google Drive · OneDrive & SharePoint · Media library
 *
 * Documents and Media library are optional (the thread reply composer has no
 * Documents picker wiring of its own today, and no media library). Cloud files
 * are attached on pick and handed back via onCloudAttached.
 *
 * Cloud providers only appear when the ACTIVE org has enabled them
 * (status.<provider>.org_enabled); org members never see a provider their org
 * turned off.
 */

import React from 'react';
import { CalendarPlus, FilePlus2, FolderOpen, Loader2, Paperclip, Upload } from 'lucide-react';
import type { UploadedAttachment } from '@/services/messagingApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { ProviderMark } from './cloud/CloudIcons';
import { MicrosoftFileBrowserDialog } from './cloud/MicrosoftFileBrowserDialog';
import { CloudAttachError, useCloudAttach } from './cloud/useCloudAttach';
import { useCloudFilesStatus } from './cloud/useCloudConnect';

interface AttachMenuProps {
  channelId: number;
  disabled?: boolean;
  onUploadClick: () => void;
  onDocumentsClick?: () => void;
  onMediaClick?: () => void;
  onCloudAttached: (attachment: UploadedAttachment) => void;
  onError: (error: CloudAttachError) => void;
  /** TRUS-691: "Schedule meeting" (staff, meetings on). */
  onScheduleMeeting?: () => void;
}

export function AttachMenu({
  channelId,
  disabled,
  onUploadClick,
  onDocumentsClick,
  onMediaClick,
  onCloudAttached,
  onError,
  onScheduleMeeting,
}: AttachMenuProps) {
  const { t } = useAppTranslation();
  const cloud = useCloudAttach({ channelId, onAttached: onCloudAttached, onError });
  const { data: cloudStatus } = useCloudFilesStatus();
  const showGoogle = !!cloudStatus?.google_drive?.org_enabled;
  const showMicrosoft = !!cloudStatus?.microsoft_files?.org_enabled;
  const busy = cloud.busy !== null;
  const reconnectProvider = cloud.reconnectFor === 'google_drive' ? 'Google Drive' : 'Microsoft';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            disabled={disabled}
            title={t('messaging.composer.attach', 'Attach')}
            aria-label={t('messaging.composer.attach', 'Attach')}
            data-testid="attach-menu-trigger"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60">
          <DropdownMenuItem onSelect={onUploadClick} className="cursor-pointer">
            <Upload className="mr-2 h-4 w-4" />
            {t('messaging.composer.uploadFromComputer', 'Upload from computer')}
          </DropdownMenuItem>
          {onDocumentsClick && (
            <DropdownMenuItem onSelect={onDocumentsClick} className="cursor-pointer">
              <FilePlus2 className="mr-2 h-4 w-4" />
              {t('messaging.composer.documents', 'Documents')}
            </DropdownMenuItem>
          )}
          {(showGoogle || showMicrosoft) && <DropdownMenuSeparator />}
          {showGoogle && (
          <DropdownMenuItem
            onSelect={() => void cloud.attachFromGoogle()}
            disabled={busy}
            className="cursor-pointer"
          >
            <ProviderMark provider="google_drive" className="mr-2" />
            {t('messaging.composer.googleDrive', 'Google Drive')}
          </DropdownMenuItem>
          )}
          {showMicrosoft && (
            <DropdownMenuItem onSelect={() => void cloud.openMicrosoft()} disabled={busy} className="cursor-pointer">
              <ProviderMark provider="onedrive" className="mr-2" />
              {t('messaging.composer.oneDriveSharePoint', 'OneDrive & SharePoint')}
            </DropdownMenuItem>
          )}
          {onMediaClick && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onMediaClick} className="cursor-pointer">
                <FolderOpen className="mr-2 h-4 w-4" />
                {t('messaging.composer.mediaLibrary', 'Media library')}
              </DropdownMenuItem>
            </>
          )}
          {onScheduleMeeting && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onScheduleMeeting} className="cursor-pointer" data-testid="composer-schedule-meeting">
                <CalendarPlus className="mr-2 h-4 w-4" />
                {t('messaging.meetings.scheduleMeeting', 'Schedule meeting…')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <MicrosoftFileBrowserDialog
        open={cloud.msOpen}
        onOpenChange={cloud.setMsOpen}
        accountEmail={cloud.msEmail}
        onConfirm={cloud.attachMicrosoftItems}
        onReconnect={() => {
          cloud.setMsOpen(false);
          void cloud.connectMicrosoft().then((ok) => ok && cloud.setMsOpen(true));
        }}
      />

      <Dialog open={cloud.reconnectFor !== null} onOpenChange={(o) => !o && cloud.setReconnectFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('messaging.cloud.reconnectTitle', 'Reconnect {{provider}}', { provider: reconnectProvider })}</DialogTitle>
            <DialogDescription>
              {cloud.reconnectMessage
                ? cloud.reconnectMessage
                : t('messaging.cloud.reconnectNeeded', 'Your {{provider}} connection has expired. Reconnect to continue.', {
                    provider: reconnectProvider,
                  })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => cloud.setReconnectFor(null)}>
              {t('messaging.channel.cancel', 'Cancel')}
            </Button>
            <Button onClick={() => void cloud.reconnect()}>
              {cloud.reconnectMessage
                ? t('messaging.cloud.useAnotherAccount', 'Connect a different account')
                : t('messaging.cloud.reconnect', 'Reconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AttachMenu;
